import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from 'sonner';
import { UserCheck, Check, X, RefreshCw } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

export default function RequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/approver-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAction = async (requestId, action) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/approver-requests/${requestId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Request ${action}ed`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} request`);
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');

  return (
    <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Approver Requests</h2>
        <Button variant="outline" size="sm" onClick={fetchRequests} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>
      
      {loading ? (
        <div className="space-y-3">
          {[1,2].map(i => (
            <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : pendingRequests.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <UserCheck className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>No pending requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingRequests.map((request) => (
            <div 
              key={request.request_id} 
              className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-semibold">
                  {request.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div>
                  <p className="font-medium text-slate-800">{request.name}</p>
                  <p className="text-sm text-slate-500">{request.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm"
                  onClick={() => handleRequestAction(request.request_id, 'approve')}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1"
                >
                  <Check className="w-4 h-4" />
                  Approve
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => handleRequestAction(request.request_id, 'reject')}
                  className="text-red-500 border-red-200 hover:bg-red-50 gap-1"
                >
                  <X className="w-4 h-4" />
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
